import { EventArg, NavigationAction, useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { useEffect } from "react";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import { useSetAlert } from "../../hooks/use-set-alert";

type ScreenParams = StackScreenProps<RootStackParamList>;

/**
 * Utility to avoid users to go back if the current form is dirty (changed, but not saved), on any screen that this hook is called
 * Inspired by https://reactnavigation.org/docs/preventing-going-back/
 */
export const useCloseEditor = (isDirty: boolean) => {
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const i18n = useI18n();
    const { setAlert } = useSetAlert();

    const { marginSize } = useTheme();

    useEffect(() => {
        const beforeRemove = (
            e: EventArg<
                "beforeRemove",
                true,
                {
                    action: NavigationAction;
                }
            >,
        ) => {
            if (!isDirty) {
                // If we don't have unsaved changes, then we don't need to do anything
                return;
            }

            e.preventDefault();

            setAlert({
                title: i18n.t("screens.profile_editor.close_warning_title"),
                message: i18n.t("screens.profile_editor.close_warning_description"),
                buttons: [
                    {
                        text: i18n.t("screens.profile_editor.quit"),
                        onPress: () => {
                            navigation.dispatch(e.data.action);
                        },
                        style: "destructive",
                    },
                    {
                        text: i18n.t("screens.profile_editor.continue"),
                        style: "cancel",
                    },
                ],
                options: { cancelable: true },
            });
        };

        navigation.addListener("beforeRemove", beforeRemove);

        return () => {
            navigation.removeListener("beforeRemove", beforeRemove);
        };
    }, [i18n, isDirty, marginSize, navigation, setAlert]);

    return null;
};
